import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { capitalize } from '@attain-apps/string-utils'

import * as state from '../../../../../../state'
import * as errorMessage from '../../error-message'

/**
 * Modal used for creating and editing Admin Center app pages.
 */
@ngCore.Component({
  imports: [
    ds.button.Component,
    ds.input.select.singleEc.Component,
    ds.input.text.Component,
    ds.input.checkbox.Component,
    ds.input.number.Component,
    ds.toggleButton.Component,
    ds.overlay.centered.Component,
    ds.layout.widget.Component,
    errorMessage.Component,
  ],
  selector: 'atn-admin-app-page-modal',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.admin.appPages.Collection} */
  public readonly appPagesEc = ngCore.inject(state.admin.appPages.Collection)

  /** @see {@link state.admin.pages.Collection} */
  public readonly pagesEc = ngCore.inject(state.admin.pages.Collection)

  /**
   * Exposed page selection target from state
   *
   * @see {@link state.admin.pages.SELECTION_TARGET}
   */
  public readonly selectionTarget = state.admin.pages.SELECTION_TARGET

  /**  ID of the currently selected {@link ents.core.admin.App.Page} */
  private readonly _appPageIdSg = ngCore.computed(
    (
      appPage = this.appPagesEc.plugins.currentEntityPlugin.feature.selectors
        .getEditedEntityAndId()
        .asSignal(),
    ) => appPage.id,
  )

  // TODO: Remove this once the effect is removed (handle the logic using the association plugin instead)
  /** ID of the currently selected {@link ents.core.mosaic.Page} */
  private readonly _selectedPageIdSg = ngCore.computed(() =>
    this.pagesEc.plugins.selectionPlugin.feature.selectors
      .getFirstSelectedId({ target: this.selectionTarget })
      .asSignal(),
  )

  constructor() {
    // TODO: Remove this effect and handle the logic using the association plugin instead
    ngCore.effect(() => {
      const pageId = this._selectedPageIdSg()
      if (!pageId) {
        return
      }
      this.updateAppPage('page', { id: pageId })
    })
  }

  /**
   * Updates a specific property of the
   * {@link ents.core.admin.App.Page.DisplayInfo} for the currently edited
   * {@link ents.core.admin.App.Page}.
   */
  public updateAppPageDisplayInfo<
    K extends keyof ents.core.admin.App.Page.DisplayInfo,
  >(prop: K, value: ents.core.admin.App.Page.DisplayInfo[K]): void {
    this.appPagesEc.plugins.editingPlugin.feature.actions.edit
      .from(`Dialog.DisplayInfo.${capitalize(prop)}.Inputted`)
      .dispatch({
        edit: appPage => {
          if (!appPage?.display) {
            return
          }

          appPage.display[prop] = value
        },
        id: this._appPageIdSg(),
      })
  }

  /**
   * Updates a specific property of the currently edited
   * {@link ents.core.admin.App.Page}.
   */
  public updateAppPage<
    K extends Exclude<keyof ents.core.admin.App.Page.Input, 'id'>,
  >(prop: K, value: ents.core.admin.App.Page.Input[K]): void {
    this.appPagesEc.plugins.editingPlugin.feature.actions.edit
      .from(`Dialog.${capitalize(prop)}.Inputted`)
      .dispatch({
        edit: appPage => {
          if (!appPage) {
            return
          }

          // TODO: Why appPage type is not already recognized as the
          // CreateEntity type?
          // Casted because "page" property does not exist in Entity (App.Page)
          // type but only in CreateEntity(App.Page.Input)
          ;(
            appPage as typeof appPage &
              Pick<ents.core.admin.App.Page.Input, 'page'>
          )[prop] = value
        },
        id: this._appPageIdSg(),
      })
  }

  /**
   * Updates a display property of the currently edited
   * {@link ents.core.admin.App.Page}.
   */
  public updateAppPageDisplayValue(value: boolean): void {
    this.appPagesEc.plugins.editingPlugin.feature.actions.edit
      .from(`Dialog.Display.Inputted`)
      .dispatch({
        edit: appPage => {
          if (!appPage) {
            return
          }

          // When updating "display" property, it means that toggle button for
          // visibility is either switched to true - in which case default
          // value need to be assigned to "display" property, or false - in
          // which case undefined should be assigned.
          // NOTE: Different handler is responsible for updating properties
          // of "display"
          appPage.display = value
            ? ents.core.admin.App.Page.DisplayInfo.getDefault()
            : undefined
        },
        id: this._appPageIdSg(),
      })
  }

  /** Submit changes handler */
  public saveClickHandler = () => {
    const source = 'Dialog.SaveButton.Clicked'
    this.appPagesEc.plugins.editingPlugin.feature.actions.submit
      .from(source)
      .dispatch({ current: true })
  }

  /** Discard changes handler */
  public cancelClickHandler = () => {
    const source = 'Dialog.CancelButton.Clicked'
    this.appPagesEc.plugins.editingPlugin.feature.actions.discard
      .from(source)
      .dispatch({
        current: true,
      })
  }
}
